import { useState } from "react";
import { AlertTriangle, Loader2, ShieldOff, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useResetUserMfa } from "../hooks/useUsers";

const BORDER = "hsl(210,30%,85%)";

type Props = {
  open: boolean;
  onClose: () => void;
  targetUserId: string;
  targetName: string;
  onSuccess?: () => void;
};

export function ResetMfaDialog({
  open,
  onClose,
  targetUserId,
  targetName,
  onSuccess,
}: Props) {
  const reset = useResetUserMfa(targetUserId);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  async function submit() {
    setError(null);
    try {
      await reset.mutateAsync();
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset two-factor authentication");
    }
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40">
      <div
        className="w-[460px] rounded-xl border bg-white p-6 shadow-xl"
        style={{ borderColor: BORDER }}
      >
        <h3 className="text-[15px] font-semibold text-foreground">
          Reset Two-Factor Authentication for {targetName}
        </h3>
        <p className="mt-1 text-[12px] text-muted-foreground">
          Removes their authenticator app and all recovery codes. They will sign
          in with their password only, and can re-enable 2FA from their Profile.
        </p>
        <div
          className="mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-[12px]"
          style={{
            borderColor: "hsl(38,92%,75%)",
            background: "hsl(38,92%,96%)",
            color: "hsl(38,92%,25%)",
          }}
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.8} />
          <span>
            Only do this after confirming the request really comes from{" "}
            {targetName} (in person or on a call) — this is how account
            takeovers usually happen.
          </span>
        </div>
        {error && (
          <div
            className="mt-3 rounded-md border px-3 py-2 text-sm"
            style={{
              borderColor: "hsl(0,70%,80%)",
              background: "hsl(0,70%,96%)",
              color: "hsl(0,70%,35%)",
            }}
          >
            {error}
          </div>
        )}
        <div className="mt-4 flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setError(null);
              onClose();
            }}
            disabled={reset.isPending}
          >
            <X className="mr-1 h-4 w-4" strokeWidth={1.5} />
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={submit} disabled={reset.isPending}>
            {reset.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} />
            ) : (
              <ShieldOff className="mr-1 h-4 w-4" strokeWidth={1.5} />
            )}
            Reset 2FA
          </Button>
        </div>
      </div>
    </div>
  );
}
